import { useCallback } from "react";
import type { EntityRef } from "@common/schemas/chatEntityRef.schema";
import { useScope } from "@state/scopeContext.lib";
import { parseTaskLink } from "./parseTaskLink";
import { useTaskLookup } from "./useEntityRefLookups";

/**
 * Turns pasted text into a task reference when it is a link to a task in the
 * current workspace that the user can see. Anything else stays a plain link.
 */
export function useTaskLinkPasteResolver(): (text: string) => EntityRef | null {
    const { workspaceId } = useScope();
    const lookup = useTaskLookup();

    return useCallback(
        (text) => {
            const link = parseTaskLink({
                text,
                origin: window.location.origin,
            });
            if (!link || !workspaceId) {
                return null;
            }
            if (link.workspaceId && link.workspaceId !== workspaceId) {
                return null;
            }
            const task = lookup?.get(link.taskId);
            return task
                ? { kind: "task", id: task.id, label: task.name }
                : null;
        },
        [workspaceId, lookup],
    );
}
